import type { GroupProps, SlotRecipeProps } from '@chakra-ui/react';
import { Avatar as ChakraAvatar, Group } from '@chakra-ui/react';
import React from 'react';

type ImageProps = React.ImgHTMLAttributes<HTMLImageElement>;

export interface AvatarProps extends ChakraAvatar.RootProps {
  name?: string;
  src?: string;
  srcSet?: string;
  loading?: ImageProps['loading'];
  icon?: React.ReactElement;
  fallback?: React.ReactNode;
  fallbackSize?: string;
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(function Avatar(props, ref) {
  const { name, src, srcSet, loading, icon, fallback, fallbackSize, children, ...rest } = props;
  const [imageLoaded, setImageLoaded] = React.useState(false);

  return (
    <ChakraAvatar.Root ref={ref} bg={imageLoaded && src ? 'transparent' : undefined} {...rest}>
      <AvatarFallback name={name} icon={icon} fallbackSize={fallbackSize}>
        {fallback}
      </AvatarFallback>
      <ChakraAvatar.Image
        src={src}
        srcSet={srcSet}
        loading={loading}
        onLoad={() => setImageLoaded(true)}
      />
      {children}
    </ChakraAvatar.Root>
  );
});

interface AvatarFallbackProps extends ChakraAvatar.FallbackProps {
  name?: string;
  icon?: React.ReactElement;
  fallbackSize?: string;
}

const AvatarFallback = React.forwardRef<HTMLDivElement, AvatarFallbackProps>(
  function AvatarFallback(props, ref) {
    const { name, icon, children, fallbackSize, ...rest } = props;
    return (
      <ChakraAvatar.Fallback ref={ref} {...rest}>
        {children}
        {name != null && children == null && <>{getInitials(name)}</>}
        {name == null && children == null && (
          <ChakraAvatar.Icon asChild={!!icon} boxSize={fallbackSize}>
            {icon}
          </ChakraAvatar.Icon>
        )}
      </ChakraAvatar.Fallback>
    );
  },
);

function getInitials(name: string) {
  const names = name.trim().split(' ');
  const firstName = names[0] != null ? names[0] : '';
  const lastName = names.length > 1 ? names[names.length - 1] : '';
  return firstName && lastName
    ? `${firstName.charAt(0)}${lastName.charAt(0)}`
    : firstName.charAt(0);
}

interface AvatarGroupProps extends GroupProps, SlotRecipeProps<'avatar'> {}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  function AvatarGroup(props, ref) {
    const { size, variant, borderless, ...rest } = props;
    return (
      <ChakraAvatar.PropsProvider value={{ size, variant, borderless }}>
        <Group gap='0' spaceX='-3' ref={ref} {...rest} />
      </ChakraAvatar.PropsProvider>
    );
  },
);
